import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { UATConstants } from 'src/Common/Models/UATConstants';
import { MANAGE_VIEW_API_CONSTANTS } from '../../utils/ManageViewConstants';
import { IViewFolder } from '../../interfaces/CustomViewFolder';
import {
  ICustomFilterQuery,
  IDeleteViewPayload,
  IViewCustomFilterResponse,
} from '../../interfaces/Slice.types';
import { IWorkItem } from 'src/Common/manageViews/interfaces/WorkItem';
import { ToolNameModel } from 'src/Models/ToolNameModel';
import { IManageViewActions } from 'src/Common/dashboardSummary/store/typings';

export const viewApi = createApi({
  reducerPath: 'viewApi',
  baseQuery: fetchBaseQuery({
    baseUrl: UATConstants.WebApiUrl,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem(UATConstants.jwtTokenForApi);
      const coreAPIToken = sessionStorage.getItem(UATConstants.jwtTokenForCoreApi);
      const graphToken = sessionStorage.getItem(UATConstants.jwtTokenForGraph);

      if (token && graphToken && coreAPIToken) {
        headers.set('Authorization', `Bearer ${token}`);
        headers.set('Coreapitoken', coreAPIToken);
        headers.set('Graphtoken', graphToken);
      }
      return headers;
    },
  }),
  tagTypes: ['ViewFolders', 'CustomFilters'],
  endpoints: (builder) => ({
    getViewWithFoldersData: builder.query<IViewFolder[], string>({
      query: () => ({
        method: 'POST',
        url: MANAGE_VIEW_API_CONSTANTS.GetViewsWithFolders,
      }),
      providesTags: ['ViewFolders'],
    }),
    getCustomViewFoldersData: builder.query<IViewCustomFilterResponse, string>({
      query: () => ({
        method: 'GET',
        url: MANAGE_VIEW_API_CONSTANTS.GetViews,
      }),
      providesTags: ['CustomFilters'],
    }),
    getPreviewDataGrid: builder.mutation<IManageViewActions, object>({
      query: (data) => ({
        method: 'POST',
        url: MANAGE_VIEW_API_CONSTANTS.GetRunQueryData,
        body: data,
      }),
    }),
    getAllDataGrid: builder.mutation<IWorkItem, object>({
      query: (data) => ({
        method: 'POST',
        url: MANAGE_VIEW_API_CONSTANTS.GetAllData,
        body: data,
      }),
    }),
    getActionsCount: builder.mutation<IWorkItem, object>({
      query: (data) => ({
        method: 'POST',
        url: MANAGE_VIEW_API_CONSTANTS.GetActionsCount,
        body: data,
      }),
    }),
    updateCustomViewFolder: builder.mutation<ICustomFilterQuery, ICustomFilterQuery>({
      query: (body) => ({
        method: 'PUT',
        url: MANAGE_VIEW_API_CONSTANTS.UpdateCustomQuery,
        body,
      }),
      invalidatesTags: ['ViewFolders', 'CustomFilters'],
    }),
    updateAddViewToFavoritesData: builder.mutation<void, string | undefined>({
      query: (data) => ({
        method: 'PUT',
        url: `${MANAGE_VIEW_API_CONSTANTS.AddViewToFavorites}/${data}`,
        body: data,
      }),
    }),
    updateRemoveViewFromFavoritesData: builder.mutation<void, string | undefined>({
      query: (data) => ({
        method: 'PUT',
        url: `${MANAGE_VIEW_API_CONSTANTS.RemoveViewFromFavorites}/${data}`,
        body: data,
      }),
    }),
    createTemporaryView: builder.mutation<IViewFolder, string | undefined>({
      query: (data) => ({
        method: 'POST',
        url: `${MANAGE_VIEW_API_CONSTANTS.CreateTemporaryView}/?MainViewId=${data}`,
      }),
    }),
    updateMarkAsDefault: builder.mutation<void, string | undefined>({
      query: (data) => ({
        method: 'PUT',
        url: `${MANAGE_VIEW_API_CONSTANTS.MarkViewAsDefault}/${data}`,
      }),
    }),
    deleteFolder: builder.mutation<unknown, string | undefined>({
      query: (folderId) => ({
        method: 'DELETE',
        url: `${MANAGE_VIEW_API_CONSTANTS.DeleteFolder}/${folderId}`,
      }),
      invalidatesTags: ['ViewFolders', 'CustomFilters'],
    }),
    deleteUserView: builder.mutation<void, IDeleteViewPayload>({
      query: (view) => ({
        method: 'DELETE',
        url: `${MANAGE_VIEW_API_CONSTANTS.DeleteUserView}/${view.id}/${view.ViewCategory}`,
      }),
      invalidatesTags: ['ViewFolders', 'CustomFilters'],
    }),
    saveCustomQuery: builder.mutation<ICustomFilterQuery, ICustomFilterQuery>({
      query: (body) => ({
        method: 'POST',
        url: MANAGE_VIEW_API_CONSTANTS.SaveCustomQuery,
        body,
      }),
      invalidatesTags: ['ViewFolders', 'CustomFilters'],
    }),
    createFolder: builder.mutation<IViewFolder, IViewFolder>({
      query: (body) => ({
        method: 'POST',
        url: MANAGE_VIEW_API_CONSTANTS.CreateFolder,
        body,
      }),
      invalidatesTags: ['ViewFolders', 'CustomFilters'],
    }),
  }),
});

export const {
  useGetViewWithFoldersDataQuery,
  useGetCustomViewFoldersDataQuery,
  useGetPreviewDataGridMutation,
  useUpdateCustomViewFolderMutation,
  useUpdateAddViewToFavoritesDataMutation,
  useUpdateRemoveViewFromFavoritesDataMutation,
  useCreateTemporaryViewMutation,
  useUpdateMarkAsDefaultMutation,
  useDeleteFolderMutation,
  useDeleteUserViewMutation,
  useSaveCustomQueryMutation,
  useCreateFolderMutation,
  useGetActionsCountMutation,
  useGetAllDataGridMutation,
} = viewApi;
